/**
 * Radar chart builder (ADR-0079 §4.1, §4.4).
 *
 * `encoding.indicators` defines the axes (one indicator column per axis).
 * Without `encoding.series` → one polygon for the FIRST row.
 * With `encoding.series` → one polygon per distinct value of `series.column`,
 * each row contributing its indicator values.
 */

import type { RadarSpec } from "../echartsSpec.js";
import {
  buildNoDataGraphic,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

export function buildRadarOption(
  spec: RadarSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const indicatorIdxs = spec.encoding.indicators.map((ind) =>
    resolveColumnIndex(ind.column, dimensions),
  );
  const seriesIdx = spec.encoding.series
    ? resolveColumnIndex(spec.encoding.series.column, dimensions)
    : null;

  // ECharts radar.indicator: array of { name, max?, min? }
  const indicators = spec.encoding.indicators.map((ind) => ({
    name: ind.column,
    ...(ind.min !== undefined ? { min: ind.min } : {}),
    ...(ind.max !== undefined ? { max: ind.max } : {}),
  }));

  // Build polygons: one per row (or one per distinct series value).
  type Polygon = { name: string; value: Array<number | null> };
  const polygons: Polygon[] = [];

  if (indicatorIdxs.every((i) => i !== null)) {
    if (seriesIdx !== null) {
      // Group by series.column; each row contributes to the named polygon.
      const byKey = new Map<string, Polygon>();
      for (const row of rows) {
        const key = row[seriesIdx] == null ? "(unknown)" : String(row[seriesIdx]);
        if (!byKey.has(key)) {
          byKey.set(key, {
            name: key,
            value: indicatorIdxs.map((idx) => coerce(row[idx as number])),
          });
        } else {
          // Multiple rows per series → take last seen (per-row authoring is the
          // expected shape; multi-row-per-series is unusual but stable).
          byKey.set(key, {
            name: key,
            value: indicatorIdxs.map((idx) => coerce(row[idx as number])),
          });
        }
      }
      for (const p of byKey.values()) polygons.push(p);
    } else {
      for (let i = 0; i < rows.length; i++) {
        polygons.push({
          name: `Row ${i + 1}`,
          value: indicatorIdxs.map((idx) => coerce(rows[i][idx as number])),
        });
      }
    }
  }

  const palette = resolvePalette(spec.options.color, polygons.length);

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    ...(palette ? { color: palette } : {}),
    legend: { show: spec.options.legend?.show !== false },
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "item",
    },
    radar: { indicator: indicators },
    series: [
      {
        type: "radar",
        data: polygons,
      },
    ],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
